import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Phone, Volume2, X } from "lucide-react";
import { DialogOverlay, DialogPortal } from "@/components/ui/dialog";
import { useIdleTrigger } from "@/hooks/useIdleTrigger";
import { MILITARY_FACTS } from "@/lib/militaryFacts";
import { SITE, STATS } from "@/lib/site";
import { MEDIA } from "./BrandVideo";
import { FactDeck } from "./FactDeck";

const IDLE_MS = 60_000;
const SEEN_KEY = "757-idle-film-seen";

const seen = () => {
  try {
    return sessionStorage.getItem(SEEN_KEY) === "1";
  } catch {
    return false;
  }
};
const markSeen = () => {
  try {
    sessionStorage.setItem(SEEN_KEY, "1");
  } catch {
    /* storage blocked: the pop-up may show again next idle minute */
  }
};
/** Don't interrupt a visitor who is in another dialog or watching a video with sound. */
const busyElsewhere = () =>
  Boolean(document.querySelector('[role="dialog"], [role="alertdialog"], [data-concierge-open="true"]')) ||
  Array.from(document.querySelectorAll("video")).some((v) => !v.paused && !v.muted);

/** Rob's film starts with sound when the browser allows it; otherwise muted with a "Turn sound on" button below the frame. */
const PopupFilm = () => {
  const [muted, setMuted] = useState(false);
  const [video, setVideo] = useState(null);
  const attach = useCallback((v) => {
    setVideo(v);
    if (!v) return;
    v.muted = false;
    v.play().catch(() => {
      v.muted = true;
      setMuted(true);
      v.play().catch(() => {});
    });
  }, []);
  const unmute = () => {
    if (!video) return;
    video.muted = false;
    video.play().catch(() => {});
    setMuted(false);
  };
  return (
    <div className="bg-black lg:col-start-1 lg:row-start-1">
      <video ref={attach} src={MEDIA.rob.src} poster={MEDIA.rob.poster} controls playsInline preload="auto" onVolumeChange={(e) => setMuted(e.currentTarget.muted)}
        className="block aspect-video w-full bg-black object-contain" aria-label={MEDIA.rob.label} data-testid="idle-film-video" />
      {muted && (
        <button type="button" onClick={unmute} data-testid="idle-film-sound"
          className="flex w-full items-center justify-center gap-2 bg-white/5 py-3 text-[14px] text-white transition-colors hover:bg-white/10">
          <Volume2 className="h-4 w-4 text-brass" /> Turn sound on
        </button>
      )}
    </div>
  );
};

/** The team's published numbers (from 757realty.com), under the film on desktop and after the facts on phones. */
const TeamNumbers = () => (
  <div className="border-t border-white/10 bg-black/40 p-6 sm:px-8 lg:col-start-1 lg:row-start-2" data-testid="idle-film-stats">
    <p className="eyebrow !text-white/70">The Rob Resh Team by the numbers</p>
    <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4">
      {STATS.map((s) => (
        <div key={s.id}>
          <p className="font-display text-2xl text-brass">{s.value}</p>
          <p className="mt-1 text-[13px] leading-snug text-white/75">{s.label}</p>
        </div>
      ))}
    </div>
  </div>
);

/** After 60 seconds without any activity, shows Rob's film with military home-buying facts (once per visit). Closing leaves the page exactly as it was. */
export const IdleFilmPopup = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const enabled = !pathname.startsWith("/admin") && !open && !seen();

  useIdleTrigger(IDLE_MS, () => {
    if (seen() || busyElsewhere()) return;
    markSeen();
    setOpen(true);
  }, enabled);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPortal>
        <DialogOverlay className="bg-black/75 backdrop-blur-sm" />
        <DialogPrimitive.Content data-testid="idle-film-popup" aria-describedby="idle-film-desc"
          className="fixed left-1/2 top-1/2 z-50 max-h-[92svh] w-[calc(100%-1.5rem)] max-w-6xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-md bg-ink text-white shadow-2xl focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95">
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-white/10 bg-ink px-5 py-3">
            <p className="eyebrow !text-brass">A minute with The Rob Resh Team</p>
            <DialogPrimitive.Close data-testid="idle-film-close"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-white/30 px-4 text-[13px] text-white transition-colors hover:bg-white hover:text-ink">
              <X className="h-4 w-4" /> Close
            </DialogPrimitive.Close>
          </div>
          <div className="grid lg:grid-cols-[1.5fr_1fr] lg:grid-rows-[auto_1fr]">
            <PopupFilm />
            <div className="p-6 sm:p-8 lg:col-start-2 lg:row-span-2 lg:row-start-1">
              <DialogPrimitive.Title className="font-display text-3xl font-normal leading-tight">Buying with the military in mind</DialogPrimitive.Title>
              <p id="idle-film-desc" className="mb-6 mt-2 text-[14px] text-white/75">Quick facts for service members, veterans and their families.</p>
              <FactDeck facts={MILITARY_FACTS} testId="idle-facts" />
              <p className="mt-5 text-[12px] leading-relaxed text-white/60">General information, not lending or tax advice. Your lender and the VA confirm eligibility.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <DialogPrimitive.Close data-testid="idle-film-keep-browsing"
                  className="inline-flex h-11 items-center rounded-full bg-brass px-6 text-[14px] font-medium text-ink transition-colors hover:bg-white">
                  Keep browsing
                </DialogPrimitive.Close>
                <Link to="/military#pcs-form" onClick={() => setOpen(false)} data-testid="idle-film-pcs"
                  className="inline-flex h-11 items-center rounded-full border border-white/40 px-6 text-[14px] text-white transition-colors hover:bg-white hover:text-ink">
                  Start my PCS plan
                </Link>
              </div>
              <a href={SITE.phoneMainHref} data-testid="idle-film-call" className="mt-5 inline-flex items-center gap-2 text-[14px] text-white underline decoration-brass underline-offset-4">
                <Phone className="h-4 w-4" /> Call {SITE.phoneMain}
              </a>
              <p className="mt-3 text-[13px] text-white/70">Close anytime and you'll be right where you left off.</p>
            </div>
            <TeamNumbers />
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
};
